/**
 * 角色管理页面
 * 对接后端 system_app API
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Tag,
  message,
  Popconfirm,
  Tree,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  TeamOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_BASE_URL, API_ENDPOINTS } from '../../api/config';

interface Role {
  id: number;
  name: string;
  code: string;
  description?: string;
  status: number;
  sort: number;
  created_at?: string;
  permissions: Permission[];
}

interface Permission {
  id: number;
  name: string;
  code: string;
  type: string;
}

const RoleList: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [permModalVisible, setPermModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedPermIds, setSelectedPermIds] = useState<number[]>([]);
  const [form] = Form.useForm();

  const getToken = () => localStorage.getItem('token') || '';

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  // 加载角色列表
  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.roles}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      } else {
        const error = await response.json();
        message.error(error.detail || '加载失败');
      }
    } catch (error) {
      message.error('网络错误');
    } finally {
      setLoading(false);
    }
  };

  // 加载所有权限
  const loadPermissions = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.permissions}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setAllPermissions(data.permissions || []);
      }
    } catch (error) {
      console.error('加载权限失败', error);
    }
  };

  // 新增角色
  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ sort: 0 });
    setModalVisible(true);
  };

  // 编辑角色
  const handleEdit = (role: Role) => {
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      code: role.code,
      description: role.description,
      sort: role.sort,
    });
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async (values: any) => {
    try {
      const url = editingRole
        ? `${API_BASE_URL}${API_ENDPOINTS.system_app.roleDetail(editingRole.id)}`
        : `${API_BASE_URL}${API_ENDPOINTS.system_app.roles}`;
      
      const response = await fetch(url, {
        method: editingRole ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        message.success(editingRole ? '更新成功' : '新增成功');
        setModalVisible(false);
        loadRoles();
      } else {
        const error = await response.json();
        message.error(error.detail || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 删除角色
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.roleDetail(id)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      if (response.ok) {
        message.success('删除成功');
        loadRoles();
      } else {
        const error = await response.json();
        message.error(error.detail || '删除失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 打开分配权限弹窗
  const handlePermission = (role: Role) => {
    setSelectedRole(role);
    setSelectedPermIds(role.permissions.map(p => p.id));
    setPermModalVisible(true);
  };

  // 保存权限分配
  const handlePermissionSave = async () => {
    if (!selectedRole) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.roleDetail(selectedRole.id)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            permission_ids: selectedPermIds,
          }),
        }
      );

      if (response.ok) {
        message.success('权限分配成功');
        setPermModalVisible(false);
        loadRoles();
      } else {
        const error = await response.json();
        message.error(error.detail || '分配失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns: ColumnsType<Role> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '角色名称',
      dataIndex: 'name',
      width: 120,
    },
    {
      title: '角色编码',
      dataIndex: 'code',
      width: 120,
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: '权限数',
      key: 'permCount',
      width: 100,
      render: (_, record) => (
        <Tag color="blue">{record.permissions?.length || 0} 个</Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 70,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time: string) =>
        time ? new Date(time).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<LockOutlined />}
            onClick={() => handlePermission(record)}
          >
            权限
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该角色？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 构建权限树
  const permissionTreeData = allPermissions.map(p => ({
    key: p.id,
    title: `${p.name} (${p.code})`,
  }));

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <TeamOutlined />
            <span>角色管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadRoles}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增角色
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          pagination={{
            total: roles.length,
            pageSize: 20,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* 新增/编辑角色弹窗 */}
      <Modal
        title={editingRole ? '编辑角色' : '新增角色'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="角色名称"
            name="name"
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="如：运营管理员" />
          </Form.Item>

          <Form.Item
            label="角色编码"
            name="code"
            rules={[{ required: true, message: '请输入角色编码' }]}
          >
            <Input placeholder="如：operator" disabled={!!editingRole} />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <Input.TextArea rows={3} placeholder="角色描述" />
          </Form.Item>

          <Form.Item label="排序" name="sort">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 分配权限弹窗 */}
      <Modal
        title={`分配权限 - ${selectedRole?.name}`}
        open={permModalVisible}
        onCancel={() => setPermModalVisible(false)}
        onOk={handlePermissionSave}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          <Tree
            checkable
            checkedKeys={selectedPermIds}
            onCheck={(checked) => setSelectedPermIds(checked as number[])}
            treeData={permissionTreeData}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Tag color="blue">已选 {selectedPermIds.length} 个权限</Tag>
        </div>
      </Modal>
    </div>
  );
};

export default RoleList;
